import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon, faPalette, faSun, faCrown } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useTheme } from './ThemeContext';
import { useT } from '../i18n/I18nContext';
import { fetchQuotaStatus } from '../util/quotaApi';

const ICONS = {
  light: faSun,
  dark: faMoon,
  playful: faPalette,
};

// The catalogue (``THEMES`` in ThemeContext) ships English label +
// description strings. Translating them at the source would force every
// consumer to know about i18n; instead this helper looks up the
// localised label/description per theme id, falling back to whatever
// the catalogue ships with when the key is missing. ``t(missing-key)``
// returns the key itself (see ``I18nContext.t``), so we have to
// compare against the key to detect a missing translation.
const lookup = (t, key, fallback) => {
  const v = t(key);
  return v === key ? fallback : v;
};
const localized = (t, theme) => ({
  label: lookup(t, `settings.appearance.theme.${theme.id}`, theme.label),
  description: lookup(
    t,
    `settings.appearance.theme.${theme.id}.desc`,
    theme.description,
  ),
});

/**
 * Compact variant — a single icon button that cycles through themes.
 * Used in the navbar so the theme control takes ~32px instead of the
 * ~180px the pill-radio version consumes.
 */
function ThemeToggle({ className = '' }) {
  const t = useT();
  const { theme, cycleTheme, themes } = useTheme();
  const current = themes.find((th) => th.id === theme) ?? themes[0];
  const { label } = localized(t, current);
  const icon = ICONS[theme] ?? faPalette;
  return (
    <button
      type="button"
      onClick={cycleTheme}
      title={`${t('settings.appearance.theme')}: ${label}`}
      aria-label={t('settings.appearance.current', { label })}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-text hover:bg-surfaceAlt hover:text-primary transition ${className}`}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  );
}

// Per-theme styling for the swatch chip itself. Playful gets the
// trademark sticker shadow so a user previewing themes can already
// *see* the identity before clicking. Light + dark use a plain
// rounded chip — consistent shape, just different texture.
const swatchChipClass = (themeId) =>
  themeId === 'playful'
    ? 'inline-flex h-3.5 w-7 overflow-hidden rounded-full border-2 border-[color:rgb(var(--color-ink))] shadow-[0_1.5px_0_rgb(var(--color-ink))]'
    : 'inline-flex h-3 w-6 overflow-hidden rounded-full border border-border';

export default function ThemeSwitcher({ className = '', compact = false }) {
  const t = useT();
  const { theme, setTheme, themes } = useTheme();
  // Premium-only themes (like "warm") render with a small crown
  // icon for free writers and tapping them surfaces an upsell
  // toast instead of switching the theme. Tier comes from the
  // shared quota status — same source the trial banner uses.
  const [tier, setTier] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const status = await fetchQuotaStatus();
      if (!cancelled && status?.tier) setTier(status.tier);
    })();
    return () => { cancelled = true; };
  }, []);
  const canUsePremium = tier === 'premium' || tier === 'basic';

  if (compact) return <ThemeToggle className={className} />;

  const handlePick = (th) => {
    if (th.premium_only && !canUsePremium) {
      toast.info(
        t('settings.appearance.theme.premium_locked', { label: th.label })
        || `${th.label} is part of Premium — upgrade to unlock.`,
      );
      return;
    }
    setTheme(th.id);
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('settings.appearance.label')}
      // ``flex flex-wrap`` instead of ``inline-flex`` so a narrow
      // Settings column (especially mobile) wraps the 5 theme pills
      // onto multiple rows instead of forcing horizontal overflow
      // and clipping the right-most options. ``rounded-2xl`` reads
      // better than ``rounded-full`` once we've broken to multiple
      // rows — the pill aesthetic only works on a single line.
      className={`flex flex-wrap items-center gap-1 rounded-2xl p-1.5 app-surface ${className}`}
    >
      {themes.map((th) => {
        const active = th.id === theme;
        const { label, description } = localized(t, th);
        const locked = th.premium_only && !canUsePremium;
        return (
          <button
            key={th.id}
            type="button"
            role="radio"
            aria-checked={active}
            title={locked ? `${description} (Premium-only)` : description}
            onClick={() => handlePick(th)}
            className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition ${
              active
                ? 'bg-primary text-white shadow-soft'
                : locked
                  ? 'text-secondText hover:bg-surfaceAlt'
                  : 'text-text hover:bg-surfaceAlt'
            }`}
          >
            <span aria-hidden className={swatchChipClass(th.id)}>
              {th.swatch.map((color, i) => (
                <span
                  key={i}
                  style={{ backgroundColor: color }}
                  className="block h-full flex-1"
                />
              ))}
            </span>
            {label}
            {locked ? (
              <FontAwesomeIcon
                icon={faCrown}
                className="text-[10px] text-accent"
                aria-label="Premium-only"
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
